import { PayloadAction } from '@reduxjs/toolkit';

import {
    CqmMeasureConfigurationClient,
    CqmMeasureConfigurationDto,
    CqmMeasureValueSetBindingDto,
    NextCycleSelectionEntry,
    UpdateNextCycleSelectionRequest,
} from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { AdminState } from './models';

// Action-type constants resolved statically so the thunks below don't depend on
// adminSlice.actions resolution order at module load (matches the pattern in
// slice.cqm-capability.ts — jest hits the slice cycle before adminSlice is fully
// created; production hits it after).
const SET_MEASURE_CONFIGURATION = 'admin/setCqmMeasureConfiguration';
const SET_VALUE_SET_BINDINGS = 'admin/setCqmValueSetBindings';
const SET_ERROR = 'admin/setError';

export const reducers = {
    setCqmMeasureConfiguration: (state: AdminState, action: PayloadAction<CqmMeasureConfigurationDto>) => {
        state.cqmMeasureConfiguration = action.payload;
        state.errorMessage = null;
    },
    setCqmValueSetBindings: (state: AdminState, action: PayloadAction<CqmMeasureValueSetBindingDto>) => {
        state.cqmValueSetBindings = {
            ...state.cqmValueSetBindings,
            [action.payload.cmsId]: action.payload,
        };
        state.errorMessage = null;
    },
};

export function fetchCqmMeasureConfiguration(): AppThunk {
    return async (dispatch: AppDispatch) => {
        const client = new CqmMeasureConfigurationClient(null, Axios);

        try {
            const response = await client.getConfiguration();

            if (response.result.succeeded) {
                dispatch({ type: SET_MEASURE_CONFIGURATION, payload: response.result.entity });
            } else {
                handleError(response, () => dispatch({ type: SET_ERROR, payload: null }));
            }
        } catch (error) {
            handleError(error, () => dispatch({ type: SET_ERROR, payload: error.toString() }));
        }
    };
}

export function fetchCqmValueSetBindings(cmsId: string): AppThunk {
    return async (dispatch: AppDispatch) => {
        const client = new CqmMeasureConfigurationClient(null, Axios);

        try {
            const response = await client.getValueSetBindings(cmsId);

            if (response.result.succeeded) {
                dispatch({ type: SET_VALUE_SET_BINDINGS, payload: response.result.entity });
            } else {
                handleError(response, () => dispatch({ type: SET_ERROR, payload: null }));
            }
        } catch (error) {
            handleError(error, () => dispatch({ type: SET_ERROR, payload: error.toString() }));
        }
    };
}

export function saveCqmNextCycleSelection(selections: NextCycleSelectionEntry[]): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch) => {
        const client = new CqmMeasureConfigurationClient(null, Axios);

        try {
            const dto = new UpdateNextCycleSelectionRequest();
            dto.selections = selections;

            const response = await client.updateNextCycleSelection(dto);

            if (response.result.succeeded) {
                dispatch({ type: SET_MEASURE_CONFIGURATION, payload: response.result.entity });
            } else {
                handleError(response, () => dispatch({ type: SET_ERROR, payload: null }));
            }

            return response.result.succeeded;
        } catch (error) {
            handleError(error, () => dispatch({ type: SET_ERROR, payload: error.toString() }));
        }

        return false;
    };
}

export const selectCqmMeasureConfiguration = (state: RootState) => state.admin.cqmMeasureConfiguration;

export const selectCqmValueSetBindingsFor = (cmsId: string) =>
    (state: RootState): CqmMeasureValueSetBindingDto | undefined => state.admin.cqmValueSetBindings[cmsId];
